import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [bannedUntil, setBannedUntil] = useState(null);
  const [banReason, setBanReason] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const { theme, toggleTheme } = useTheme();
  const { toast } = useToast();
  const { login } = useAuth();
  const { mergeCart, fetchCart } = useCart();
  const navigate = useNavigate();

  // Countdown timer for banned users
  React.useEffect(() => {
    if (isBanned && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isBanned, timeRemaining]);

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return 'Ban expired - please try logging in again';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}, ${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}, ${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}, ${secs} second${secs > 1 ? 's' : ''}`;
    } else {
      return `${secs} second${secs > 1 ? 's' : ''}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const result = await login(email, password);
    
    if (result.success) {
      // Merge guest cart with user cart after successful login
      const guestCartId = localStorage.getItem('cart_id');
      if (guestCartId) {
        console.log('Merging guest cart after login...');
        await mergeCart();
        // Clear guest cart_id after merge
        localStorage.removeItem('cart_id');
        console.log('Cleared guest cart_id from localStorage');
      }
      
      // Fetch user cart (backend will use user_id from token)
      console.log('Fetching user cart after login...');
      await fetchCart();
      
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${result.user.name}!`,
      });
      
      // Redirect based on role
      if (result.user.role === 'admin') {
        navigate('/admin'); // Admin goes to admin dashboard
      } else if (result.user.role === 'trainer') {
        navigate('/trainer/dashboard'); // Trainer goes to trainer dashboard
      } else {
        navigate('/user/dashboard');
      }
    } else {
      // Check if user is banned
      if (result.message === 'USER_BANNED' && result.banned_until) {
        setIsBanned(true);
        setBannedUntil(result.banned_until);
        setBanReason(result.ban_reason || 'Violated terms of service');
        
        // Calculate time remaining in seconds
        const bannedDate = new Date(result.banned_until);
        const now = new Date();
        const secondsRemaining = Math.floor((bannedDate - now) / 1000);
        setTimeRemaining(secondsRemaining > 0 ? secondsRemaining : 0);
        
        return;
      }
      
      // Check if account not verified
      if (result.message === 'ACCOUNT_NOT_VERIFIED') {
        toast({
          title: 'Email Not Verified',
          description: 'Please verify your email address using the link sent to your inbox.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Login Failed',
          description: result.message || 'Invalid credentials',
          variant: 'destructive',
        });
      }
    }
  };

  // If user is banned, show banned screen
  if (isBanned) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-red-300/20 to-orange-300/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-red-300/20 to-gray-400/20 rounded-full blur-3xl"></div>
        
        <div className="w-full max-w-2xl relative z-10 p-8">
          <div className="bg-white dark:bg-gray-800/90 backdrop-blur-md rounded-2xl p-10 shadow-2xl border-2 border-red-200">
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-center text-red-600 mb-4">
              Account Suspended
            </h1>

            {/* Ban Reason */}
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
              <p className="text-center text-gray-700 mb-2">
                <span className="font-semibold">Reason:</span>
              </p>
              <p className="text-center text-lg font-medium text-red-700">
                {banReason}
              </p>
            </div>

            {/* Countdown Timer */}
            <div className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-8 mb-6">
              <p className="text-center text-gray-700 mb-3 font-semibold">
                Your account is suspended until:
              </p>
              <p className="text-center text-2xl font-bold text-orange-600 mb-4">
                {new Date(bannedUntil).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
              
              {timeRemaining > 0 && (
                <>
                  <div className="flex justify-center mb-3">
                    <div className="w-16 h-1 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></div>
                  </div>
                  <p className="text-center text-sm text-gray-600 mb-2">Time remaining:</p>
                  <div className="text-center">
                    <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600 animate-pulse">
                      {formatTimeRemaining(timeRemaining)}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Info Message */}
            <div className="bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-gray-800 text-center">
                <strong>ℹ️ Important:</strong> If you believe this suspension was made in error, please contact our support team at{' '}
                <a href="mailto:welcome@trainerlist.com" className="underline font-semibold">
                  welcome@trainerlist.com
                </a>
              </p>
            </div>

            {/* Back to Login Button */}
            <div className="flex justify-center gap-3">
              <Button
                onClick={() => {
                  setIsBanned(false);
                  setEmail('');
                  setPassword('');
                }}
                className="bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-bold px-8 py-3 rounded-xl shadow-lg"
              >
                Back to Login
              </Button>
              {timeRemaining === 0 && (
                <Button
                  onClick={() => {
                    setIsBanned(false);
                    handleSubmit({ preventDefault: () => {} });
                  }}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold px-8 py-3 rounded-xl shadow-lg"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Illustration */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-red-600 via-gray-800 to-gray-900 items-center justify-center p-12 relative overflow-hidden">
        {/* Animated background circles */}
        <div className="absolute top-10 left-10 w-64 h-64 bg-yellow-300/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-red-600/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        
        <div className="text-center relative z-10">
          <div className="mb-8">
            <img 
              src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'} 
              alt="TrainerList Logo" 
              className="w-80 h-80 mx-auto drop-shadow-2xl object-contain"
            />
          </div>
          <h2 className="text-4xl font-black text-white mb-4 drop-shadow-lg">Welcome to TrainerList</h2>
          <p className="text-gray-100 text-lg font-medium">Connect with the best trainers for your fitness journey ✨</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 relative overflow-hidden">
        {/* Theme Toggle Button */}
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 z-20 p-3 rounded-full bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-red-200 dark:border-gray-600 hover:scale-110"
          title={theme === 'dark' ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
        >
          {theme === 'dark' ? (
            <Sun className="w-5 h-5 text-yellow-500" />
          ) : (
            <Moon className="w-5 h-5 text-gray-700" />
          )}
        </button>

        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-red-300/20 to-gray-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-gray-300/20 to-red-300/20 rounded-full blur-3xl"></div>
        
        <div className="w-full max-w-md relative z-10">
          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block transform hover:scale-105 transition-transform">
              <img 
                src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'} 
                alt="TrainerList" 
                className="h-16 sm:h-20 md:h-24 w-auto object-contain mx-auto"
              />
            </Link>
            <p className="text-red-700 dark:text-red-400 mt-4 text-lg font-semibold">Login with your email ✨</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-8 shadow-2xl border-2 border-red-200 dark:border-gray-700">
            <div>
              <Label htmlFor="email" className="text-red-700 dark:text-red-400 font-semibold">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 border-2 border-red-200 focus:border-red-500 rounded-xl"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-red-700 dark:text-red-400 font-semibold">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-2 border-red-200 focus:border-red-500 rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-700 transition-colors"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  className="border-red-500 data-[state=checked]:bg-red-600"
                />
                <label htmlFor="remember" className="text-sm text-red-700 dark:text-red-400 cursor-pointer font-medium">
                  Remember me?
                </label>
              </div>
              <Link to="/forgot-password" className="text-sm text-red-600 hover:text-red-800 hover:underline font-semibold">
                Forgot Password?
              </Link>
            </div>

            <Button type="submit" className="w-full bg-gradient-to-r from-red-600 via-red-700 to-red-800 hover:from-red-700 hover:via-red-800 hover:to-red-900 text-white font-bold py-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all">
              Login ✨
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-red-700 dark:text-red-400 font-medium">
              Don't have an account?{' '}
              <Link to="/register" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 font-bold hover:underline">
                Sign up here ✨
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;